// react
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui icons
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';

// material ui
import { Box, Grid, Skeleton, Stack, useTheme } from '@mui/material';

// api
import {
  useGetBasicEnergyAccountQuery,
  useGetBasicEnergyDataQuery,
  usePutBasicEnergyManualRunMutation,
} from '@/api/energy-analysis/basicEnergySlice';
import { useUnitsGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/units';

// feature-components
import AggregationSelector from '@/features/dashboards/components/AggregationSelector';
import DashboardDateRangeSelector from '@/features/dashboards/components/DashboardDateRangeSelector';
import DashboardToolbar from '@/features/dashboards/components/DashboardToolbar';
import LatestRefreshFooter from '@/features/dashboards/components/LatestRefreshFooter';

// components
import { useAlertProvider } from '@/hooks/useAlertProvider';
import useSearchParams from '@/hooks/useStableSearchParams';
import { showSnackbar } from '@/redux/snackbarSlice';
import { formatNumber } from '@/utils/formatters';

// relative
import ColumnChart from '../components/ColumnChart';
import DashboardCard from '../components/DashboardCard';
import DashboardDatagrid from '../components/DashboardDatagrid';
import Kpi from '../components/Kpi';
import StackedDataToggle from '../components/StackedDataToggle';

import { DateTime } from 'luxon';

const POLLING_INTERVAL_MS = 60 * 1000;

const HeatBasic = () => {
  const { t } = useTranslation('dashboards-heat-basic');
  const { accountId } = useParams({ strict: false });
  const [searchParams] = useSearchParams();
  const theme = useTheme();

  const startTimestamp = searchParams.get('from');
  const endTimestamp = searchParams.get('to');
  const agg = searchParams.get('agg') || 'day';
  const unitIds = searchParams.get('units');

  const [putBasicEnergyManualRun] = usePutBasicEnergyManualRunMutation();
  const dispatch = useDispatch();
  const handleRefresh = async () => {
    const response = await putBasicEnergyManualRun({
      account_id: accountId,
      analysis_type: 'heat',
    });
    if (response.error) {
      // Error handled by apiErrorMiddleware (snackbar + App Insights).
      return;
    }
    dispatch(
      showSnackbar({
        message: t('messages.refreshStarted'),
        severity: 'success',
      }),
    );
  };

  const { data: basicEnergySubscription } = useGetBasicEnergyAccountQuery(
    { account_id: accountId },
    {
      skip: !accountId,
    },
  );

  const isUpdating = useMemo(
    () => Boolean(basicEnergySubscription?.HeatManualRunRequest),
    [basicEnergySubscription],
  );

  const {
    data: BasicEnergyData,
    isLoading,
    isFetching,
    isError,
    // error,
  } = useGetBasicEnergyDataQuery(
    {
      analysisType: 'heat',
      accountId,
      startTimestamp,
      endTimestamp,
      aggregateFrequency: agg,
      unitIds: unitIds && unitIds !== 'none' ? unitIds.split(',') : [],
    },
    {
      skip: !accountId || !startTimestamp || !endTimestamp,
      pollingInterval: isUpdating ? POLLING_INTERVAL_MS : 0,
    },
  );

  useAlertProvider({
    enabled: isError,
    severity: 'error',
    message: t('messages.loadError'),
  });

  const { data: units } = useUnitsGetCollectionAsyncByqueryOptionsQuery(
    { 'Account.AccountId': `eq(${accountId})` },
    {
      skip: !accountId,
    },
  );

  // table columns rely on translations, so define them inside the component
  const tableColumns = [
    {
      field: 'name',
      headerName: t('table.columns.name'),
      flex: 1,
      renderCell: (params) => {
        const hasTags = params.row.tags && params.row.tags.length > 0;
        return (
          <Stack spacing={0.5} sx={{ py: hasTags ? 1 : 0.5 }}>
            <Box>{params.value}</Box>
            {hasTags && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {params.row.tags.map((tag, index) => (
                  <Box
                    key={index}
                    sx={{
                      bgcolor: 'primary.light',
                      color: 'primary.contrastText',
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {tag.Text}
                  </Box>
                ))}
              </Stack>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'sum',
      headerName: t('table.columns.sum'),
      type: 'number',
      flex: 0.4,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => formatNumber(value),
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          {formatNumber(params.value)}
        </Box>
      ),
    },
    {
      field: 'maxh',
      headerName: t('table.columns.maxh'),
      type: 'number',
      flex: 0.3,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => formatNumber(value),
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          {formatNumber(params.value)}
        </Box>
      ),
    },
  ];

  const chartData = useMemo(() => {
    if (!BasicEnergyData) return { categories: [], series: [] };

    const useStackedData = searchParams.get('stacked') === 'true';

    if (useStackedData && BasicEnergyData?.StackedData) {
      // Use StackedData for multi-series view
      // Each item in StackedData has: { UnitId, UnitName, DataType, DataPoints: [{ timestamp, sum, mean, min, max }] }
      const series = BasicEnergyData.StackedData.map((unit) => ({
        name: unit.UnitName || `Unit ${unit.UnitId}`,
        data: unit.DataPoints.map((dataPoint) => [
          DateTime.fromISO(dataPoint.timestamp).toMillis(),
          dataPoint.sum || 0,
        ]).sort((a, b) => a[0] - b[0]),
      }));

      return { categories: [], series };
    }

    // Use AggregatedData for single series view
    if (!BasicEnergyData?.AggregatedData) return { categories: [], series: [] };

    const sorted = [...BasicEnergyData.AggregatedData].sort(
      (a, b) =>
        DateTime.fromISO(a.timestamp).toMillis() -
        DateTime.fromISO(b.timestamp).toMillis(),
    );
    const series = [
      {
        name: t('chart.series.consumption'),
        data: sorted.map((d) => [
          DateTime.fromISO(d.timestamp).toMillis(),
          d.sum,
        ]),
      },
    ];
    return { categories: [], series };
  }, [BasicEnergyData, t, searchParams]);

  const tableData = useMemo(() => {
    const rows = BasicEnergyData?.MeterSummaries?.map((ms) => {
      const unit = units?.find((u) => u.UnitId === ms.UnitId);
      return {
        id: ms.UnitId,
        tags: unit?.Tags || [],
        name: ms.UnitName,
        sum: ms.TotalValue,
        maxh: ms.MaxValue,
      };
    });
    return rows || [];
  }, [BasicEnergyData, units]);

  const totalConsumption = useMemo(() => {
    if (!BasicEnergyData || !BasicEnergyData?.MeterSummaries) return 0;

    let summaries = BasicEnergyData.MeterSummaries;

    // Filter by selected units if any are specified in URL
    if (unitIds && unitIds !== 'none') {
      const selectedIds = unitIds.split(',').map((id) => Number(id) || id);
      summaries = summaries.filter((ms) => selectedIds.includes(ms.UnitId));
    } else if (unitIds === 'none') {
      return 0; // No units selected
    }

    return summaries.reduce((acc, ms) => acc + (ms.TotalValue || 0), 0);
  }, [BasicEnergyData, unitIds]);

  if (isLoading || !BasicEnergyData) {
    return (
      <Box>
        <DashboardToolbar
          title={t('title')}
          rightSlot={
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                gap: 2,
                flexDirection: 'row',
              }}
            >
              <DashboardDateRangeSelector />
            </Box>
          }
        />
        <Grid container spacing={2} alignItems="stretch">
          <Grid
            size={{
              xs: 12,
              md: 5
            }}>
            <Stack gap={2} height="100%">
              <Skeleton variant="rectangular" animation="wave" height={120} />
              <Skeleton variant="rectangular" animation="wave" height={360} />
            </Stack>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 7
            }}>
            <Skeleton variant="rectangular" animation="wave" height={600} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <DashboardToolbar
        title={t('title')}
        rightSlot={
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              gap: 2,
              flexDirection: 'row',
            }}
          >
            <DashboardDateRangeSelector />
          </Box>
        }
      />
      <Grid container spacing={2} alignItems="stretch">
        <Grid
          size={{
            xs: 12,
            md: 5
          }}>
          <Stack gap={2} height="100%">
            <DashboardCard
              title={t('cards.totalConsumption')}
              icon={<SpeedRoundedIcon />}
              iconColor={theme.palette.heat.main}
              iconBgColor={theme.palette.heat.light}
            >
              <Kpi
                label={t('kpi.label')}
                value={formatNumber(totalConsumption)}
                unit={t('kpi.unit')}
              />
            </DashboardCard>
            <DashboardCard
              title={t('cards.powerUnits')}
              icon={<SensorsRoundedIcon />}
              iconColor={theme.palette.heat.main}
              iconBgColor={theme.palette.heat.light}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
              }}
            >
              <DashboardDatagrid
                rows={tableData}
                columns={tableColumns}
                getRowHeight={() => 'auto'}
              />
            </DashboardCard>
          </Stack>
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 7
          }}>
          <DashboardCard
            icon={<BarChartRoundedIcon />}
            iconColor={theme.palette.heat.main}
            iconBgColor={theme.palette.heat.light}
            title={t('cards.consumptionByDay')}
            actions={
              <Stack direction="row" spacing={2} alignItems="center">
                <StackedDataToggle namespace="dashboards-heat-basic" />
                <AggregationSelector />
              </Stack>
            }
          >
            <ColumnChart
              data={chartData}
              color={theme.palette.heat.main}
              unit={t('kpi.unit')}
              height={600}
              isLoading={isFetching}
              isStacked={searchParams.get('stacked') === 'true'}
            />
          </DashboardCard>
        </Grid>
      </Grid>
      <LatestRefreshFooter
        latestRefresh={BasicEnergyData?.LastCacheTimestamp}
        isUpdating={isUpdating}
        onRefresh={handleRefresh}
      />
    </Box>
  );
};

export default HeatBasic;
